/**
 * CategoryCombobox — Searchable category/sub-category selector
 * Uses position:fixed portal (same approach as Gantt popup) to GUARANTEE
 * the dropdown always appears ABOVE the trigger, never clipped by viewport.
 * Does NOT rely on Radix Popover positioning.
 * 
 * CRITICAL: No autoFocus on search input — iPad Safari auto-scrolls the page
 * when an input receives focus, which causes the "page jumps to bottom" bug.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp, Check, Search } from 'lucide-react';

export function CategoryCombobox({ value, options, onChange, placeholder = '+ Category' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const savedScrollRef = useRef(0);
  const selectedLabel = options.find(o => o.value === value)?.label || '';

  // Calculate position: ALWAYS above trigger using position:fixed
  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownH = dropdownRef.current?.offsetHeight || 280;
    const dropdownW = 280;

    // Always try above first
    let top = rect.top - dropdownH - 6;
    let left = rect.left;

    // Only go below if literally zero space above (trigger at very top of screen)
    if (top < 8) {
      top = rect.bottom + 6;
    }
    // Clamp within viewport
    if (left + dropdownW > window.innerWidth - 10) {
      left = window.innerWidth - dropdownW - 10;
    }
    if (left < 10) left = 10;
    // Clamp top to never go above viewport
    if (top < 8) top = 8;
    // Clamp bottom to never go below viewport
    if (top + dropdownH > window.innerHeight - 10) {
      top = window.innerHeight - dropdownH - 10;
    }

    setPos({ top, left });
  }, []);

  // When opening: save scroll, calculate position, prevent scroll changes
  const handleOpen = useCallback(() => {
    savedScrollRef.current = window.scrollY;
    setSearch('');
    setOpen(true);
  }, []);

  // Restore scroll if it shifted
  useEffect(() => {
    if (!open) return;
    // Initial position calc
    calcPosition();
    // Restore scroll position immediately (iPad Safari may shift it)
    const restoreScroll = () => {
      if (Math.abs(window.scrollY - savedScrollRef.current) > 5) {
        window.scrollTo(0, savedScrollRef.current);
      }
    };
    // Check multiple times in case of delayed scroll
    const t1 = setTimeout(restoreScroll, 50);
    const t2 = setTimeout(restoreScroll, 150);
    const t3 = setTimeout(restoreScroll, 300);
    // Also recalc position after scroll restore
    const t4 = setTimeout(calcPosition, 100);
    const t5 = setTimeout(calcPosition, 350);

    // Listen for scroll/resize to recalc
    const handleReposition = () => calcPosition();
    window.addEventListener('resize', handleReposition);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, calcPosition]);

  // Recalculate after dropdown renders (now we know actual height)
  useEffect(() => {
    if (open && dropdownRef.current) {
      requestAnimationFrame(calcPosition);
    }
  }, [open, calcPosition]);

  // Close on outside click/touch
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', handleOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', handleOutside);
    };
  }, [open]);

  // Filter options
  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (optValue) => {
    onChange(optValue);
    setOpen(false);
    setSearch('');
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => open ? setOpen(false) : handleOpen()}
        className="inline-flex items-center gap-1 text-left rounded transition-colors cursor-pointer text-xs font-bold text-stone-900 px-2 py-1 bg-stone-100 border border-stone-300 hover:bg-stone-200 hover:border-stone-400"
        data-testid="category-combobox-trigger"
      >
        <span className="truncate max-w-[160px]">{selectedLabel || placeholder}</span>
        <ChevronUp size={11} className="opacity-50 flex-shrink-0" />
      </button>

      {open && createPortal(
        <>
          {/* Invisible backdrop to catch outside clicks */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onPointerDown={() => setOpen(false)}
          />
          {/* Dropdown panel — position:fixed, portaled to body */}
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: pos.top + 'px',
              left: pos.left + 'px',
              zIndex: 9999,
              width: '280px',
              maxWidth: 'calc(100vw - 20px)',
            }}
            className="bg-white rounded-lg border border-stone-300 shadow-2xl"
            data-testid="category-combobox-dropdown"
          >
            {/* Search — NO autoFocus to prevent iPad Safari scroll jump */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-200 bg-stone-50 rounded-t-lg">
              <Search size={14} className="text-stone-400 flex-shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to search..."
                className="w-full text-sm bg-transparent focus:outline-none placeholder:text-stone-400"
              />
            </div>
            {/* Options list */}
            <div className="max-h-[220px] overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-sm text-stone-400 text-center">No category found.</div>
              ) : (
                filtered.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm rounded-md cursor-pointer transition-colors ${
                      value === opt.value
                        ? 'bg-blue-50 text-blue-800 font-bold'
                        : 'text-stone-800 hover:bg-stone-100 font-medium'
                    }`}
                  >
                    <Check size={14} className={`flex-shrink-0 ${value === opt.value ? 'opacity-100 text-blue-600' : 'opacity-0'}`} />
                    <span>{opt.label}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
